import React from 'react';
import {
  AfterSubmitFunc,
  Cell,
  Dropdown,
  FormTemplate,
  Header3,
  List,
  LoadingAnimation,
  Text,
  TextField, useListContextRequired,
  useShowContextRequired
} from 'ui-builder';
import { AccessChecker } from 'authorization-scope';
import {
  ConnectionProfile,
  Person, PERSON_UPDATED,
  TeamPermission, useEventsSubscriber, useExternalUsersService,
  useTeamMembersService,
  useUserLinkService
} from 'nekst-api';

import {
  GridLayout,
  IconDeleteButton,
  IconType,
  InformationMessage,
  OpenFormIconButton
} from 'features/nekst-widgets';
import { useDataLoaderNew } from 'data-loader';

function useLink(profile: ConnectionProfile) {
  const showContext = useShowContextRequired<Person>();

  let link = null;
  if (showContext.data.externalUsersLinks) {
    link = showContext.data.externalUsersLinks.find(item => item.connectionProfileId === profile.id);
  }

  return {
    link
  };
}

function UserLinkField(
  props: {
    profile: ConnectionProfile,
  }
) {
  const { link } = useLink(props.profile);

  if (link) {
    return (
      <Text>
        {link._userName}
        {' ('}
        {link._userEmail}
        {')'}
      </Text>
    );
  } else {
    return (
      <Text>Not Linked</Text>
    );
  }
}

function UnlinkButton(props: { profile: ConnectionProfile }) {
  const { link } = useLink(props.profile);

  const showContext = useShowContextRequired<Person>();

  const userLinkService = useUserLinkService();

  if (link) {
    return (
      <IconDeleteButton
        deleteFunc={async () => {
          await userLinkService.unlinkMember(
            showContext.data.id,
            link.id
          );
        }}
        message={`Are you sure you want to unlink ${showContext.data.profile.name.fullName} from their Follow Up Boss account?`}
      />
    );
  } else {
    return null;
  }
}

function ExternalUserDropdown(props: {
  profile: ConnectionProfile,
  alreadyLinkedIds: string[]
}) {
  const externalUsersService = useExternalUsersService();

  const { data: externalUsers } = useDataLoaderNew(
    async () => externalUsersService.getAllExternalUsers(props.profile.id)
  );

  if (externalUsers) {
    const options = externalUsers
      .filter((item) => !props.alreadyLinkedIds.includes(item.id))
      .map(user => ({
        label: user.name,
        value: user.id
      }));

    return (
      <Dropdown
        options={options}
        source="externalUserId"
        label="Select Follow Up Boss User"
        isDisabled={options.length === 0}
        placeholder={options.length === 0 ? 'All Follow Up Boss users are already linked' : 'Select a Follow Up Boss user to link'}
      />
    );
  } else {
    return (<LoadingAnimation />);
  }
}

function LinkUserForm(props: {
  profile: ConnectionProfile,
  person: Person,
  afterSubmitFunc?: AfterSubmitFunc,
}) {

  const memberLinkService = useUserLinkService();

  const listContext = useListContextRequired<Person>();

  const alreadyLinkedUsersIds = listContext
    .data
    .map((item) => item.externalUsersLinks)
    .flat()
    .filter((item) => item.connectionProfileId === props.profile.id)
    .map((item) => item.externalUserId);

  return (
    <FormTemplate
      title="Link to a Follow Up Boss User"
      submitFormFunc={async (data) => {
        await memberLinkService.linkMember(props.person.id, {
          externalUserId: data.externalUserId,
          connectionProfileId: props.profile.id
        });
        return data;
      }}
      validationSchema={{
        externalUserId: {
          type: 'string',
          constraints: {
            required: true
          }
        }
      }}
      afterSubmitFunc={props.afterSubmitFunc}
    >
      <ExternalUserDropdown
        profile={props.profile}
        alreadyLinkedIds={alreadyLinkedUsersIds}
      />
    </FormTemplate>
  );
}

function LinkButton(props: { profile: ConnectionProfile }) {
  const { link } = useLink(props.profile);

  const showContext = useShowContextRequired<Person>();

  if (link) {
    return null;
  } else {
    return (
      <OpenFormIconButton
        form={<LinkUserForm profile={props.profile} person={showContext.data} />}
        type={IconType.PENCIL}
      />
    );
  }
}

function TeamMembersList(props: {
  profile: ConnectionProfile,
}) {
  const teamMembersService = useTeamMembersService();

  const [version, setVersion] = React.useState(0);
  useEventsSubscriber('TeamMembersList', {
    [PERSON_UPDATED]: () => {
      setTimeout(() => {
        setVersion((prev) => prev + 1);
      }, 100);
    }
  });

  return (
    <List
      getDataFunc={teamMembersService.getTeamMembers}
      dependencies={[version]}
    >
      <GridLayout>
        <Cell label="Member Name">
          <TextField source="profile.name.fullName" />
        </Cell>
        <Cell label="Linked to FUB Account">
          <UserLinkField profile={props.profile} />
        </Cell>
        <Cell label="Actions">
          <LinkButton profile={props.profile} />
          <UnlinkButton profile={props.profile} />
        </Cell>
      </GridLayout>
    </List>
  );
}

export default function UsersLinksForm(props: {
  profile: ConnectionProfile
}) {
  return (
    <AccessChecker permissionToCheck={TeamPermission.MANAGE_FUB_CONNECTION}>
      <Header3>Link Users to FUB</Header3>
      <InformationMessage>
        Link Nekst team members to their Follow Up Boss accounts to enable syncing of deal
        participants and other user-specific data.
      </InformationMessage>
      <TeamMembersList profile={props.profile} />
    </AccessChecker>
  );
}
